/**
 * ORTHO draft-lock gate (AutoCAD ORTHOMODE / F8) — the impure wrapper around the
 * pure {@link applyOrthoConstraint} geometry helper.
 *
 * Split from `orthoConstraint.ts` (which stays a THREE-free, store-free, purely
 * unit-testable leaf) so the sysvar-store read lives in ONE place and the
 * pointer-input seam (`stageInteractions`) imports a single function instead of
 * carrying the gating inline (which pushed the already-cap-sized file over its
 * 2000-line budget). Mirrors how `pointerSnapStep` stays pure while its caller
 * owns the side effects.
 *
 * Precedence + gating (all must hold for the lock to apply):
 *   - `snapMagnetized` is false — object snap OVERRIDES ortho in AutoCAD, so a
 *     snapped point goes to the geometry, never the 0/90° axis. This gates on
 *     whether the emit was ACTUALLY magnetized to the snap (AUTOSNAP bit 4),
 *     NOT on mere hit presence: with the magnet bit off the resolver still
 *     reports a hit (marker/tooltip) while the emitted coord stays the raw
 *     cursor, and that raw cursor MUST still be ortho-locked (else it commits
 *     an unconstrained diagonal that is neither snapped nor axis-aligned);
 *   - `base` is non-null — an anchor ($LASTPOINT) exists; Ortho never affects
 *     the FIRST point of a command;
 *   - ortho is EFFECTIVE — `ORTHOMODE` is on OR a Shift temp-override forces it
 *     (via `selectEffectiveOrtho`, which folds the temp-override layer in).
 * Otherwise the cursor world is returned verbatim, so every ortho-off /
 * non-draft / snapped path is byte-identical to the pre-feature behaviour.
 */

import { applyOrthoConstraint } from "./orthoConstraint";
import {
  useSysvarStore,
  selectEffectiveOrtho,
} from "../../dynamicInput/sysvarStore";

/**
 * Lock a resolved cursor `world` to 0/90° from the draft `base` anchor when
 * Ortho is effective and the emit was not magnet-snapped; otherwise return
 * `world` unchanged. Reads the live `ORTHOMODE` / Shift temp-override from the
 * sysvar store via `getState()` (not a hook) at event time.
 */
export function orthoLockedCursor(
  world: { x: number; y: number },
  base: { x: number; y: number } | null,
  snapMagnetized: boolean,
): { x: number; y: number } {
  if (snapMagnetized) return world;
  if (!base) return world;
  if (!selectEffectiveOrtho(useSysvarStore.getState())) return world;
  return applyOrthoConstraint(base, world);
}
